/* eslint-disable @angular-eslint/no-output-on-prefix */
import {
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { getLocaleMonthNames, getLocaleDayNames, FormStyle, TranslationWidth } from '@angular/common';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  DatePickerListener,
  DatePickerHelper,
} from '../../client/repositories/lib/common/utils/date-picker.helper';
import { LanguageService } from '../../shared/foundation/i18n/language.service';
import { SettingsService } from '../../shared/foundation/settings/settings.service';

@Injectable()
class DynamicDatepickerI18n extends NgbDatepickerI18n {
  locale = 'de';

  getWeekdayLabel(weekday: number): string {
    const days = getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated);
    // Angular weekdays start at Sunday (0), ng-bootstrap at Monday (1)
    return days[(weekday % 7)];
  }

  getMonthShortName(month: number): string {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated)[month - 1];
  }

  getMonthFullName(month: number): string {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Wide)[month - 1];
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}.${date.month}.${date.year}`;
  }
}

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  providers: [
    { provide: NgbDatepickerI18n, useClass: DynamicDatepickerI18n },
  ],
})
export class DatepickerComponent
  implements OnInit, DatePickerListener, OnChanges, OnDestroy
{
  @Input() value?: Date;
  @Input() valid?: boolean;
  @Input() controlName?: string;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;
  @Input() noDays = false;
  @Input() readonly = true;

  @Output() onDateChange: EventEmitter<Date> = new EventEmitter<Date>();
  public datePickerHelper?: DatePickerHelper;

  ngbMinDate?: NgbDateStruct;
  ngbMaxDate?: NgbDateStruct;
  startDate?: NgbDateStruct;
  displayValue = '';

  private langSub?: Subscription;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private languageService: LanguageService,
    private i18n: NgbDatepickerI18n,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    (this.i18n as DynamicDatepickerI18n).locale = this.settingsService.getLocaleId() || 'de';
    this.setDatePickerHelper();
    this.syncDates();
    this.langSub = this.languageService.getLanguage().subscribe((lang) => {
      (this.i18n as DynamicDatepickerI18n).locale = lang;
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnChanges(): void {
    if (this.datePickerHelper) {
      this.setDatePickerHelper();
    }
    this.syncDates();
  }

  private syncDates(): void {
    if (this.value) {
      this.displayValue = this.formatDate(this.value);
      this.startDate = this.toNgbDate(this.value);
    }
    if (this.minDate) {
      this.ngbMinDate = this.toNgbDate(this.minDate);
    }
    if (this.maxDate) {
      this.ngbMaxDate = this.toNgbDate(this.maxDate);
    }
  }

  onDateSelect(ngbDate: NgbDateStruct): void {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    this.displayValue = this.formatDate(date);

    if (this.controlName) {
      const control = this.formGroupDirective.form.get(this.controlName);
      if (control) {
        control.setValue(date);
        control.markAsDirty();
      }
    }

    this.datePickerHelper?.setDate(date);
  }

  public onDateChanged(newDate: Date, helper: DatePickerHelper) {
    if (!newDate?.getDate()) {
      this.onDateChange.emit(null!);
    } else {
      this.onDateChange.emit(newDate);
    }
  }

  setDatePickerHelper(): void {
    this.datePickerHelper = new DatePickerHelper(this, null!);
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  }

  private toNgbDate(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }
}
